import { action } from 'mobx';
import { IConnectionDetailsDTO } from '../../../../../../../api/connections.dao';
import { IPhoneNumbersSet, ISortedPhoneNumbers } from '../../../../../../../api/zoom.dao';
import { AbstractFormViewModelController } from '../../../../../../../infrastructure/implementations/form-viewmodel-controller';

export interface IDropdownOption {
    value: string;
}
export interface IAudioBridgeValues {
    showDialInInfo: string;
    hideDialInInfo: string;
    showHideButtonEnabled: boolean;
}
export class AudioBridgeConfigurationVM {

    public formVM: AbstractFormViewModelController;
    public connection: IConnectionDetailsDTO;
    public phoneNumbersSet: IPhoneNumbersSet[];
    public isConnectionGroup: boolean;
    public audioBridgeValues: IAudioBridgeValues;

    public isCustomParticipantTelephoneNumbers: boolean = null;
    public transformedNumbersSet: ISortedPhoneNumbers = null;
    public countries: IDropdownOption[] = [];
    public initialCountry: IDropdownOption = null;
    public initialTelephoneNumbers: IDropdownOption[] = [];
    public initialPhone: IDropdownOption = null;

    constructor(
        formVM: AbstractFormViewModelController,
        connection: IConnectionDetailsDTO,
        phoneNumbersSet: IPhoneNumbersSet[],
        isConnectionGroup: boolean,
        audioBridgeValues: IAudioBridgeValues,
    ) {
        this.formVM = formVM;
        this.connection = connection;
        this.phoneNumbersSet = phoneNumbersSet;
        this.isConnectionGroup = isConnectionGroup;
        this.audioBridgeValues = audioBridgeValues || null;

        this.isCustomParticipantTelephoneNumbers = !!connection?.participantTelephoneNumbers;
        this.initialize();

    }

    @action
    public initialize = (): void => {
        if (this.isCustomParticipantTelephoneNumbers) {
            const participantTelephoneNumbersValue = this.connection.participantTelephoneNumbers?.split('\n') || [];
            this.initialTelephoneNumbers = participantTelephoneNumbersValue.map(value => ({ value: value }));
            const initialPhoneValue = this.connection?.participantOneTapDial || this.initialTelephoneNumbers?.[0].value || '';
            this.initialPhone = { value: initialPhoneValue };
            if (this.connection?.participantOneTapDial) {
                this.initialTelephoneNumbers.unshift({ value: this.connection.participantOneTapDial });
            }
        } else {
            this.transformedNumbersSet = this.transformAndSortNumbersSet(this.phoneNumbersSet);

            this.countries = this.getCountriesSelectOptions();
            this.initialCountry = this.countries?.length ? { value: this.countries[0].value } : null;
            this.initialTelephoneNumbers = this.initialCountry?.value
                ? this.transformedNumbersSet[this.initialCountry.value].map(value => ({ value: value.telephoneNumber }))
                : null;

            this.initialPhone = this.initialTelephoneNumbers?.[0] || null;
        }
    };

    public getCountries = (): string[] | null => {
        if (this.transformedNumbersSet) {
            return Object.keys(this.transformedNumbersSet);
        }
        return null;
    };

    public getCountriesSelectOptions = (): IDropdownOption[] => {
        const countriesList = this.getCountries();

        return countriesList?.map(item => ({
            value: item
        })) || [];
    };

    public transformAndSortNumbersSet = (phoneNumbersList: IPhoneNumbersSet[] | null | undefined): ISortedPhoneNumbers | null => {
        if (phoneNumbersList && phoneNumbersList.length > 0) {
            const transformed = phoneNumbersList.reduce<ISortedPhoneNumbers>((result, item) => {
                const countryValue = item.country;
                if (!result[countryValue]) {
                    result[countryValue] = [];
                }
                result[countryValue].push(item);
                return result;
            }, {});

            const sortedByKeys = Object.keys(transformed).sort((a, b) => {
                const priorityA = transformed[a][0].priority;
                const priorityB = transformed[b][0].priority;

                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                } else {
                    return a.localeCompare(b);
                }
            });

            const sortedObject: ISortedPhoneNumbers = {};
            sortedByKeys.forEach(key => {
                sortedObject[key] = transformed[key].sort((a, b) => a.telephoneNumber.localeCompare(b.telephoneNumber));
            });

            return sortedObject;
        }
        return null;

    };

    public getPhoneNumberSelectOptions = (phoneNumbersList: IPhoneNumbersSet[]): IDropdownOption[] => {
        return phoneNumbersList.map(item => ({
            value: item.telephoneNumber,
        }));
    };

    public getPhoneNumbersByCountry = (countryValue: string): IPhoneNumbersSet[] => {
        if (!this.transformedNumbersSet || !countryValue) return [];
        const entries = this.transformedNumbersSet[countryValue] || [];
        return entries;
    };

}